/**
 * Normalizes FastAPI error payloads into a single human-readable string.
 *
 * A FastAPI `detail` is NOT always a string — the backend sends at least three
 * shapes, and rendering any non-string one straight into JSX throws
 * "Objects are not valid as a React child", which takes out the whole route
 * via the ErrorBoundary:
 *
 *   - `"Incorrect email or password"`                      (HTTPException)
 *   - `[{ loc, msg, type }, …]`                            (422 validation)
 *   - `{ code: "license_required", message: "…" }`         (403 license gate,
 *                                                           see .claude/rules/api-design.md)
 *
 * Lives here rather than in `src/api/client.js` because that module owns
 * transport concerns (CSRF, license redirect) and is legacy `.js`; this is a
 * pure presentation-layer formatter with no axios dependency, so the raw-fetch
 * SSE path can use it too.
 */

/** Guards against a self-referential `{ detail: { detail: … } }` payload. */
const MAX_DEPTH = 4;

/** One entry of a 422 error list → readable text. */
function normalizeDetailItem(item: unknown): string | null {
  if (typeof item === "string") return item.trim() || null;
  if (typeof item === "number" || typeof item === "boolean") return String(item);
  if (item && typeof item === "object") {
    const msg = (item as { msg?: unknown }).msg;
    if (typeof msg === "string" && msg.trim()) return msg.trim();
    const message = (item as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) return message.trim();
    try {
      return JSON.stringify(item);
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Turns any `detail` value into readable text, or `null` when nothing usable
 * is in there (the caller then applies its own fallback copy).
 */
export function normalizeApiDetail(detail: unknown, depth = 0): string | null {
  if (depth > MAX_DEPTH) return null;

  if (typeof detail === "string") return detail.trim() || null;

  if (Array.isArray(detail)) {
    const parts = detail
      .map(normalizeDetailItem)
      .filter((part): part is string => Boolean(part));
    return parts.length > 0 ? parts.join("; ") : null;
  }

  if (detail && typeof detail === "object") {
    const obj = detail as Record<string, unknown>;
    if (typeof obj.message === "string" && obj.message.trim()) return obj.message.trim();
    if (typeof obj.msg === "string" && obj.msg.trim()) return obj.msg.trim();
    if ("detail" in obj) return normalizeApiDetail(obj.detail, depth + 1);
  }

  return null;
}

/**
 * Best-effort user-facing message for a rejected request: response `detail`
 * first, then the thrown error's own `message` (network/timeout errors have no
 * response), then `fallback`.
 */
export function extractApiErrorMessage(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } } | null | undefined)
    ?.response?.data?.detail;
  const fromDetail = normalizeApiDetail(detail);
  if (fromDetail) return fromDetail;

  const message = (err as { message?: unknown } | null | undefined)?.message;
  if (typeof message === "string" && message.trim()) return message.trim();

  return fallback;
}
